import { useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { useKyc } from '@/features/kyc';
import { useUser } from '@/features/user';
import { useApplication } from '@/features/application/hooks/useApplication';
import { routes } from '@/routes/routes';
import { formatAmountWithSpaces } from '@/lib/amountUtils';
import { getPreliminaryOffers } from '@/lib/offerUtils';
import { preliminaryChoiceStorage, useLoanOffer } from '../../hooks';
import { CreditProductType } from '../../types/offer';
import { type OceanOfferType } from '../../components/OceanOfferFace';
import { OceanOfferHero } from '../../components/OceanOfferHero';
import { OfferFaq } from '../../components/OfferFaq';
import { useLastStepModal } from '../../components/LastStepModal';
import { PARTNER_OFFER_CONTENT } from './partnerOfferContent';
import styles from './PartnerOfferDetailsPage.module.scss';

const PRODUCT_TYPES: Record<OceanOfferType, CreditProductType> = {
  'credit-line': CreditProductType.WhiteLabelCard,
  installment: CreditProductType.Consumer,
};

const isOceanOfferType = (type: string | undefined): type is OceanOfferType =>
  type === 'credit-line' || type === 'installment';

/**
 * Details of a preliminary partner offer, opened from the
 * preliminary offers; the amount (and the installments' duration) is chosen
 * in its calculator. Activating it continues to KYC and keeps the choice, so
 * the final offer from ANAF continues with the same credit.
 */
export function PartnerOfferDetailsPage() {
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const { isKycLoading, isKycValid } = useKyc(true);
  const { loanOfferState, isLoading } = useLoanOffer(true);
  const { user } = useUser();
  const { creditLineDraft, installmentDraft } = useApplication();
  // Set below, once the offer is known.
  const confirmRef = useRef<() => void>(undefined);
  const { open: openLastStepModal, modal: lastStepModal } = useLastStepModal({
    trackingId: `partner_offer_confirm_identity_${type}`,
    onConfirm: () => confirmRef.current?.(),
  });

  if (isKycLoading || isLoading) {
    return <PageLoader />;
  }

  const offer = isOceanOfferType(type)
    ? getPreliminaryOffers(loanOfferState?.creditOffers).find(
        (preliminaryOffer) => preliminaryOffer.type === PRODUCT_TYPES[type]
      )
    : undefined;

  // With KYC there is nothing left to apply for.
  if (!isOceanOfferType(type) || !offer || isKycValid) {
    return <Navigate to={routes.offer.preliminary} replace />;
  }

  const content = PARTNER_OFFER_CONTENT[type];
  const draft = type === 'credit-line' ? creditLineDraft : installmentDraft;
  const amount = Math.min(draft.amount ?? offer.creditLimit, offer.creditLimit);
  const numberOfRates =
    type === 'installment'
      ? draft.numberOfRates ?? offer.maxNumberOfRates
      : offer.maxNumberOfRates;
  const openCalculator = () =>
    navigate(routes.offer.preliminaryCalculator(type));

  const details = [
    {
      label: 'Suma',
      value: `${formatAmountWithSpaces(amount)} lei`,
      onEdit: openCalculator,
    },
    { label: 'Dobânda', value: `${offer.nominalAnnualPercentageRate} %` },
    {
      label: 'Durata',
      value: `${numberOfRates} luni`,
      onEdit: type === 'installment' ? openCalculator : undefined,
    },
  ];

  confirmRef.current = () => {
    if (user) {
      preliminaryChoiceStorage.save({
        userId: user.id,
        type,
        amount,
        numberOfRates: type === 'installment' ? numberOfRates : undefined,
        offer: {
          creditLimit: offer.creditLimit,
          effectiveAnnualPercentageRate: offer.effectiveAnnualPercentageRate,
        },
      });
    }
    navigate(routes.kyc.base);
  };

  return (
    <div className={styles.root}>
      <div className={styles.topBar}>
        <button
          type="button"
          className={styles.backButton}
          aria-label="Înapoi"
          onClick={() => navigate(routes.wizard.base)}
        >
          <Icon name="arrow-left" />
        </button>
      </div>

      <div className={styles.content}>
        <OceanOfferHero
          type={type}
          amount={offer.creditLimit}
          numberOfRates={offer.maxNumberOfRates}
        />

        <div className={styles.intro}>
          <div className={styles.heading}>
            <h1 className={styles.title}>
              <span className={styles.titleAccent}>{content.titleAccent}</span>
              <span>{content.title}</span>
            </h1>
            <p className={styles.description}>
              {content.description[0]}
              <br />
              {content.description[1]}
            </p>
          </div>

          <ul className={styles.usps}>
            {content.usps.map((usp) => (
              <li key={usp.text.join(' ')} className={styles.usp}>
                <img src={usp.icon} alt="" className={styles.uspIcon} />
                <p className={styles.uspText}>
                  {usp.text.map((line, index) => (
                    <span key={line}>
                      {index > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Detalii</h2>
          <dl className={styles.details}>
            {details.map((detail) => (
              <div key={detail.label} className={styles.detail}>
                <dt className={styles.detailLabel}>{detail.label}</dt>
                <dd className={styles.detailValue}>
                  {detail.value}
                  {detail.onEdit && (
                    <Button
                      trackingId={`partner_offer_edit_${detail.label.toLowerCase()}_${type}`}
                      iconOnly
                      size="xsmall"
                      variant="outlined"
                      aria-label={`Modifică ${detail.label.toLowerCase()}`}
                      leftIcon={<Icon name="pen" className={styles.editIcon} />}
                      onClick={detail.onEdit}
                      className={styles.editButton}
                    />
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Întrebări frecvente</h2>
          <OfferFaq items={content.faq} />
        </section>
      </div>

      <div className={styles.bottomSection}>
        <Button
          trackingId={`partner_offer_apply_${type}`}
          text="Activează acum"
          rightIcon={<Icon name="arrow-right" />}
          onClick={openLastStepModal}
          className={styles.applyButton}
        />
      </div>
      {lastStepModal}
    </div>
  );
}

export default PartnerOfferDetailsPage;
